import React from 'react';
import { DisplayProperty } from 'csstype';

interface ChildrenResponse {
  top: number | string;
  left: number | string;
  bottom: number | string;
  right: number | string;
}

interface AttachTooltipProps {
  offsetX: number;
  offsetY: number;
  attachDisplay?: DisplayProperty;
  content: React.ReactNode;
  attachPosition: 'top' | 'left' | 'right' | 'bottom' | 'center' | 'none';
  position?: React.CSSProperties['position'];
  alignSelf?: React.CSSProperties['alignSelf'];
  delay?: number;
  children(response: ChildrenResponse): React.ReactNode;
}

interface AttachTooltipState extends ChildrenResponse {
  mounted: boolean;
}

export default class AttachTooltip extends React.Component<AttachTooltipProps, AttachTooltipState> {
  containerRef: React.RefObject<HTMLDivElement>;

  static defaultProps = {
    offsetX: 0,
    offsetY: 0,
    delay: 0,
    attachDisplay: 'block',
  };

  constructor(props: AttachTooltipProps) {
    super(props);

    this.state = {
      top: 'auto',
      left: 'auto',
      bottom: 'auto',
      right: 'auto',
      mounted: false,
    };

    this.containerRef = React.createRef();
  }

  componentDidMount() {
    setTimeout(this.refreshTooltipPosition, this.props.delay ?? 0);

    window.addEventListener('resize', this.refreshTooltipPosition);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.refreshTooltipPosition);
  }

  componentDidUpdate(prevProps: AttachTooltipProps) {
    if (
      prevProps.offsetY !== this.props.offsetY ||
      prevProps.offsetX !== this.props.offsetX ||
      prevProps.attachPosition !== this.props.attachPosition
    ) {
      this.refreshTooltipPosition();
    }
  }

  refreshTooltipPosition = () => {
    if (!this.containerRef.current) return;
    const { offsetX, offsetY, attachPosition } = this.props;
    const rect = this.containerRef.current.getBoundingClientRect();

    let top: number | string = 'auto';
    let left: number | string = 'auto';
    let right: number | string = 'auto';
    let bottom: number | string = 'auto';

    if (attachPosition === 'bottom') {
      top = offsetY;
      left = rect.width / 2 + offsetX;
    } else if (attachPosition === 'top') {
      bottom = -offsetY;
      left = rect.width / 2 + offsetX;
    } else if (attachPosition === 'left') {
      top = rect.height / 2 + offsetY;
      right = -offsetX;
    } else if (attachPosition === 'right') {
      top = rect.height / 2 + offsetY;
      left = offsetX;
    } else if (attachPosition === 'center') {
      top = rect.height / 2 + offsetY;
      left = rect.width / 2 + offsetX;
    } else if (attachPosition === 'none') {
      top = offsetY;
      left = offsetX;
    }

    this.setState({ top, left, right, bottom, mounted: true });
  };

  render() {
    const { children, content, attachDisplay, position, alignSelf } = this.props;
    const { top, left, right, bottom, mounted } = this.state;
    const style: React.CSSProperties = { display: attachDisplay };

    if (position) {
      style.position = position;
    }

    if (alignSelf) {
      style.alignSelf = alignSelf;
    }

    return (
      <div ref={this.containerRef} style={style}>
        {mounted && children({ top, left, right, bottom })}
        {content}
      </div>
    );
  }
}
